import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Heart, Star, MessageCircle, Users, Globe, ClipboardCheck, BookOpen, Handshake, Award, GraduationCap, Briefcase, Trophy, HeartHandshake, Building, Compass, HardHat, Globe2, Quote } from "lucide-react";

const domains = [
  { icon: Heart, name: "Emotional Intelligence", desc: "Developing self-awareness, empathy, emotional regulation, and healthy relationships.", color: "#EC4899" },
  { icon: Star, name: "Leadership", desc: "Building confidence, accountability, responsibility, service, and leadership capacity.", color: "#C9A961" },
  { icon: MessageCircle, name: "Communication", desc: "Strengthening listening, collaboration, conflict resolution, and interpersonal effectiveness.", color: "#0B1E36" },
  { icon: Users, name: "Belonging", desc: "Creating environments where individuals feel respected, included, connected, and supported.", color: "#2C7A7B" },
  { icon: Globe, name: "Community Engagement", desc: "Encouraging service, civic participation, meaningful contribution, and positive community impact.", color: "#8B5CF6" },
];

const provides = [
  { icon: ClipboardCheck, title: "Assessments & Measurement", subtitle: "Future Counts Index™",
    body: "Track growth in Emotional Intelligence, Leadership, Communication, Belonging, and Community Engagement." },
  { icon: BookOpen, title: "Learning Experiences", subtitle: "Interactive training",
    body: "Leadership Development · Emotional Intelligence · Communication Skills · Conflict Resolution · Team Building · Community Engagement." },
  { icon: Handshake, title: "Partner Implementation Support", subtitle: "Resources & guidance",
    body: "Practical support to help organizations implement human development initiatives with fidelity." },
  { icon: Award, title: "Recognition & Impact", subtitle: "Celebrate growth",
    body: "Support for recognizing growth, celebrating success, and documenting long-term impact." },
];

const sectors = [
  { icon: GraduationCap, name: "Schools & School Districts" },
  { icon: Compass, name: "Colleges & Universities" },
  { icon: Briefcase, name: "Workforce Development" },
  { icon: HeartHandshake, name: "Community Organizations" },
  { icon: HardHat, name: "Labor & Union Partners" },
  { icon: Trophy, name: "Sports Organizations" },
  { icon: Building, name: "Employers & Businesses" },
  { icon: Globe2, name: "Global Partners" },
];

const initiatives = [
  { name: "EQ Partners Campaign™", desc: "A collective movement advancing human development, leadership, and belonging across sectors." },
  { name: "EQ Ambassadors for Belonging™", desc: "A school-based leadership and belonging initiative." },
  { name: "EQ Ambassadors for Success™", desc: "A workforce readiness and leadership development initiative." },
  { name: "Future Counts Index™", desc: "A measurement framework designed to assess growth across key human development domains." },
];

const outcomes = ["Valued", "Connected", "Supported", "Empowered", "Prepared to succeed"];

export default function About() {
  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-bg">
      <header className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="about-brand-link">
          <div className="h-10 w-10 rounded-xl bg-[#0B1E36] grid place-items-center">
            <Sparkles className="h-5 w-5 text-[#C9A961]" />
          </div>
          <div>
            <div className="font-serif text-xl leading-tight text-[#0B1E36]">Future Counts OS™</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">by the Future Counts Collective</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="about-back" className="text-sm font-medium text-slate-600 hover:text-[#0B1E36] inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back home</Link>
          <Link to="/signup" data-testid="about-signup" className="text-sm font-semibold bg-[#0B1E36] text-white px-5 py-2.5 rounded-full btn-lift hover:bg-[#132a4a]">Get started</Link>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 pb-20">
        <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">About</div>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#0B1E36] leading-[1.05] tracking-tight max-w-3xl">
          Building a better future through <span className="italic">human development, emotional intelligence, leadership,</span> and belonging.
        </h1>
      </section>

      {/* ── The Collective ────────────────────────────────────────────── */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-3 gap-12">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">The Collective</div>
              <div className="font-serif text-3xl text-[#0B1E36] leading-tight">A movement across schools, campuses, teams, and communities.</div>
            </div>
            <div className="lg:col-span-2 space-y-5 text-[15px] text-slate-700 leading-[1.75]">
              <p>The Future Counts Collective is a movement dedicated to advancing Human Development, Emotional Intelligence, Leadership, Communication, Belonging, and Community Engagement across schools, workplaces, universities, community organizations, and civic institutions.</p>
              <p>We believe that success is determined not only by what individuals know, but also by how effectively they communicate, collaborate, lead, build relationships, and contribute to their communities.</p>
              <p>In an increasingly complex and interconnected world, individuals need more than academic knowledge and technical skills. They also need the human skills that support healthy relationships, sound decision-making, effective leadership, workplace success, and community well-being.</p>
              <p>The Future Counts Collective was created to help address this need by bringing together educators, workforce development leaders, community organizations, universities, employers, labor partners, athletic organizations, and global partners around a shared commitment to developing the whole person.</p>
              <p>Through collaborative initiatives, strategic partnerships, leadership development opportunities, and community engagement efforts, we seek to strengthen the skills that help individuals thrive in school, work, relationships, leadership, and life.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Founder ──────────────────────────────────────────────────── */}
      <section className="bg-[#f4f4f4] text-[#0B1E36]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-5 gap-14 items-center">
          <div className="lg:col-span-2">
            <div
              className="relative w-full max-w-sm mx-auto aspect-square rounded-full overflow-hidden bg-white"
              style={{ boxShadow: "0 0 0 4px #0B1E36, 0 30px 60px -20px rgba(11,30,54,0.30)" }}
            >
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_eq-leadership-hub/artifacts/tdemudtj_Karen%20Pinckney%20Website%20Pic.png"
                alt="Karen Pinckney, Founder & Executive Director"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="text-center mt-6">
              <div className="font-serif text-2xl text-[#0B1E36]">Karen Pinckney</div>
              <div className="text-sm text-slate-600 mt-1">Founder & Executive Director</div>
              <div className="text-[10px] uppercase tracking-widest text-[#0066ff] mt-2 font-semibold">The Future Counts Collective</div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-widest text-[#0066ff] font-semibold mb-3">Founder</div>
            <h2 className="font-serif text-4xl leading-tight mb-6 text-[#0B1E36]">Karen Pinckney</h2>
            <div className="space-y-5 text-[15px] text-slate-700 leading-[1.75]">
              <p>Karen Pinckney is the Founder and Executive Director of The Future Counts Collective.</p>
              <p>Driven by a passion for education, leadership, human development, and community impact, Karen established The Future Counts Collective to help individuals and organizations strengthen the human skills necessary to thrive in today's world.</p>
              <p>Through initiatives such as the <span className="text-[#0066ff] font-semibold">EQ Partners Campaign™</span>, <span className="text-[#0066ff] font-semibold">EQ Ambassadors for Belonging™</span>, <span className="text-[#0066ff] font-semibold">EQ Ambassadors for Success™</span>, and the <span className="text-[#0066ff] font-semibold">Future Counts Index™</span>, Karen seeks to help build stronger schools, workplaces, organizations, and communities.</p>
            </div>
            <div className="mt-10 p-8 rounded-2xl bg-white border border-slate-200 relative">
              <Quote className="h-8 w-8 text-[#0066ff] mb-3" />
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Her guiding belief</div>
              <blockquote className="font-serif text-2xl lg:text-3xl leading-snug italic text-[#0B1E36]">
                "Who individuals are becoming matters as much as what they know."
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* ── The EQ Initiative ────────────────────────────────────────── */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">The Movement</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-[1.1] mb-6">The EQ Initiative™</h2>
            <p className="text-[15px] text-slate-700 leading-[1.75]">
              A movement dedicated to developing emotional intelligence, leadership, communication, belonging, and community engagement — so that individuals, organizations, and communities can thrive.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-14">
            <div className="p-8 rounded-2xl bg-[#FDFBF7] border border-slate-200" data-testid="init-purpose">
              <div className="text-xs uppercase tracking-widest text-[#EC4899] font-semibold mb-2">Purpose</div>
              <p className="text-sm text-slate-700 leading-[1.75]">To help individuals, organizations, and communities intentionally develop the emotional intelligence, communication, leadership, belonging, and relationship skills necessary to thrive in school, work, relationships, and life.</p>
            </div>
            <div className="p-8 rounded-2xl bg-[#FDFBF7] border border-slate-200" data-testid="init-why">
              <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-2">The Why</div>
              <p className="text-sm text-slate-700 leading-[1.75]">Across schools, workplaces, universities, community organizations, and athletic programs — technical skills and subject knowledge alone are not enough. People also need the human skills that make everything else work.</p>
            </div>
            <div className="p-8 rounded-2xl bg-[#0B1E36] text-white" data-testid="init-goal">
              <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-2">The Goal</div>
              <p className="text-sm text-slate-200 leading-[1.75]">To create <strong className="text-white">emotionally intelligent individuals</strong> and <strong className="text-white">emotionally intelligent environments</strong> — the conditions where personal and collective success become possible.</p>
            </div>
          </div>

          <div className="p-10 rounded-3xl bg-gradient-to-br from-[#0B1E36] to-[#132a4a] text-white text-center">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-4">One-Sentence Version</div>
            <blockquote className="font-serif text-2xl lg:text-3xl leading-snug max-w-4xl mx-auto italic">
              "The EQ Initiative™ is a movement dedicated to developing emotional intelligence, leadership, communication, belonging, and community engagement in order to help individuals, organizations, and communities thrive."
            </blockquote>
          </div>
        </div>
      </section>

      {/* ── The Ecosystem Diagram ────────────────────────────────────── */}
      <section className="bg-[#FDFBF7] border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">The Ecosystem</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-[1.1] mb-4">How it all fits together.</h2>
            <p className="text-slate-600 leading-relaxed">Every layer serves a distinct purpose — from the organization to the movement to the tools that make it real.</p>
          </div>

          <div className="flex flex-col items-center gap-2" data-testid="ecosystem-diagram">
            {/* L1 Collective */}
            <div className="w-full max-w-md p-5 rounded-2xl bg-[#0B1E36] text-white text-center shadow-[0_10px_30px_rgba(11,30,54,0.15)]">
              <div className="text-[10px] uppercase tracking-widest text-[#C9A961] font-semibold mb-1">The Organization</div>
              <div className="font-serif text-xl">Future Counts Collective</div>
            </div>
            <div className="h-8 w-px bg-slate-300" />

            {/* L2 Initiative */}
            <div className="w-full max-w-md p-5 rounded-2xl bg-white border-2 border-[#EC4899] text-center shadow-[0_10px_30px_rgba(236,72,153,0.12)]">
              <div className="text-[10px] uppercase tracking-widest text-[#EC4899] font-semibold mb-1">The Movement</div>
              <div className="font-serif text-xl text-[#0B1E36]">EQ Initiative™</div>
            </div>
            <div className="h-8 w-px bg-slate-300" />

            {/* L3 Programs - 3 columns */}
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">The Programs</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl">
              <div className="p-5 rounded-2xl bg-white border border-slate-200 text-center card-lift">
                <div className="font-serif text-base text-[#0B1E36] leading-tight">EQ Partners Campaign™</div>
                <div className="text-[11px] text-slate-500 mt-2">Building partnerships across sectors</div>
              </div>
              <div className="p-5 rounded-2xl bg-white border border-slate-200 text-center card-lift">
                <div className="font-serif text-base text-[#0B1E36] leading-tight">EQ Ambassadors for Belonging™</div>
                <div className="text-[11px] text-slate-500 mt-2">School-based implementation</div>
              </div>
              <div className="p-5 rounded-2xl bg-white border border-slate-200 text-center card-lift">
                <div className="font-serif text-base text-[#0B1E36] leading-tight">EQ Ambassadors for Success™</div>
                <div className="text-[11px] text-slate-500 mt-2">Workforce implementation</div>
              </div>
            </div>
            <div className="h-8 w-px bg-slate-300 mt-4" />

            {/* L4 OS */}
            <div className="w-full max-w-md p-5 rounded-2xl bg-[#C9A961]/15 border-2 border-[#C9A961] text-center">
              <div className="text-[10px] uppercase tracking-widest text-[#6B5228] font-semibold mb-1">The Platform & Framework</div>
              <div className="font-serif text-xl text-[#0B1E36]">Future Counts OS™</div>
            </div>
            <div className="h-8 w-px bg-slate-300" />

            {/* L5 Index */}
            <div className="w-full max-w-md p-5 rounded-2xl bg-white border border-slate-200 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">The Measurement</div>
              <div className="font-serif text-xl text-[#0B1E36]">Future Counts Index™</div>
            </div>
          </div>

          <div className="text-center mt-14 max-w-2xl mx-auto">
            <p className="text-sm text-slate-600 leading-[1.75] italic">
              Every partner can find themselves in this ecosystem — schools, campuses, employers, community organizations, labor partners, sports organizations, and global institutions.
            </p>
          </div>
        </div>
      </section>

      {/* ── About the OS ─────────────────────────────────────────────── */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">About Future Counts OS™</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-[1.1] mb-6">The platform for human development, leadership, and belonging.</h2>
            <p className="text-[15px] text-slate-700 leading-[1.75] max-w-2xl mx-auto">
              Future Counts OS™ is a comprehensive platform designed to help schools, workforce development organizations, community partners, universities, employers, and other institutions strengthen Human Development, Emotional Intelligence, Leadership, Communication, Belonging, and Community Engagement.
            </p>
            <p className="text-[15px] text-slate-700 leading-[1.75] max-w-2xl mx-auto mt-4">
              Serving as both a framework and a platform, Future Counts OS™ provides organizations with the tools, resources, assessments, learning experiences, and implementation strategies needed to help individuals thrive in school, work, relationships, leadership, and life.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-center bg-[#FDFBF7] p-10 rounded-3xl border border-slate-200">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">Why Future Counts OS™?</div>
              <h3 className="font-serif text-3xl text-[#0B1E36] leading-tight mb-4">Because academic and technical training aren't enough.</h3>
              <p className="text-slate-700 leading-[1.75] text-[15px]">
                Organizations invest significant resources in academic achievement, workforce readiness, technical training, and professional development — yet many continue to face challenges related to engagement, communication, leadership, belonging, workplace culture, and interpersonal effectiveness. Future Counts OS™ was created to help organizations intentionally develop the human skills that support long-term success and well-being.
              </p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-4">The platform helps organizations create environments where individuals feel:</div>
              <ul className="space-y-3">
                {outcomes.map((o) => (
                  <li key={o} className="flex items-center gap-3" data-testid={`outcome-${o.toLowerCase().replace(/\s+/g,'-')}`}>
                    <div className="h-7 w-7 rounded-full bg-[#EC4899] text-white grid place-items-center shrink-0">
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                    </div>
                    <span className="font-serif text-xl text-[#0B1E36]">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5-Domain Framework ───────────────────────────────────────── */}
      <section className="bg-[#FDFBF7] border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">The Framework</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-[1.1]">Five core domains of human development.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {domains.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.name} className="bg-white p-7 rounded-2xl border border-slate-200 card-lift" data-testid={`domain-${d.name.toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="h-11 w-11 rounded-xl grid place-items-center mb-4" style={{ background: d.color + "15" }}>
                    <Icon className="h-5 w-5" style={{ color: d.color }} />
                  </div>
                  <div className="font-serif text-lg text-[#0B1E36] mb-2 leading-tight">{d.name}</div>
                  <p className="text-sm text-slate-600 leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── What OS Provides ─────────────────────────────────────────── */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">What OS Provides</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-[1.1]">Four pillars of the platform.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {provides.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="p-8 rounded-2xl border border-slate-200 bg-[#FDFBF7] card-lift" data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-11 w-11 rounded-xl bg-[#0B1E36] grid place-items-center">
                      <Icon className="h-5 w-5 text-[#C9A961]" />
                    </div>
                    <div>
                      <div className="font-serif text-xl text-[#0B1E36] leading-tight">{p.title}</div>
                      <div className="text-xs uppercase tracking-widest text-[#EC4899] font-semibold mt-1">{p.subtitle}</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 8 Sectors ────────────────────────────────────────────────── */}
      <section className="bg-[#0B1E36] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">Designed for Multiple Sectors</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.1]">Future Counts OS™ can support:</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sectors.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.name} className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-testid={`sector-${s.name.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                  <Icon className="h-6 w-6 text-[#C9A961] mb-3" />
                  <div className="font-serif text-base leading-tight">{s.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Initiatives ──────────────────────────────────────────────── */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">How OS Comes to Life</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-[1.1]">Four flagship initiatives.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {initiatives.map((i, idx) => (
              <div key={i.name} className="p-8 rounded-2xl border border-slate-200 card-lift bg-[#FDFBF7]" data-testid={`initiative-${idx}`}>
                <div className="font-serif text-2xl text-[#0B1E36] leading-tight mb-3">{i.name}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{i.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Goal + Signature ─────────────────────────────────────────── */}
      <section className="bg-[#FDFBF7] border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">Our Goal</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-[#0B1E36] leading-tight mb-10">
            To help organizations intentionally develop the human skills, leadership capacity, and culture needed for individuals and communities to thrive.
          </h2>
          <div className="pt-10 border-t border-slate-200 max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Signature Statement</div>
            <div className="font-serif text-2xl lg:text-3xl text-[#0B1E36] italic leading-snug">
              Future Counts OS™ helps organizations develop <span className="text-[#EC4899] not-italic font-normal">the human side of success</span>.
            </div>
            <div className="mt-6 text-slate-500 text-sm">
              Because who individuals are becoming matters as much as what they know.
            </div>
          </div>

          <Link to="/signup" data-testid="about-cta" className="mt-14 inline-flex items-center gap-2 bg-[#EC4899] text-white px-8 py-3.5 rounded-full font-semibold btn-lift hover:bg-[#DB2777] shadow-[0_8px_24px_rgba(236,72,153,0.25)]">
            Join the Collective
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-xs text-slate-500">
          <div>© 2026 Future Counts Collective</div>
          <div>Building a better future — together.</div>
        </div>
      </footer>
    </div>
  );
}
